import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { usePendingVerseCount } from '../../hooks/useVerseSubmissions';
import { isArtistRole } from '../../types/chat';

const iconLinkClass =
  'relative inline-flex items-center justify-center h-10 w-10 rounded-xl text-text-secondary hover:text-primary hover:bg-background-tertiary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300';

const MessagesIcon: React.FC = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M8 10.5h8M8 14h5m7-2a8 8 0 01-8 8H8l-4 3v-4.5A8 8 0 1120 12z"
    />
  </svg>
);

const SubmissionsIcon: React.FC = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M4 13h4l1.5 3h5L16 13h4M4 13l2.2-6.4A2 2 0 018.1 5.2h7.8a2 2 0 011.9 1.4L20 13v4.2a1.8 1.8 0 01-1.8 1.8H5.8A1.8 1.8 0 014 17.2V13z"
    />
  </svg>
);

const ProfileIcon: React.FC = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 0c-3.6 0-6.5 2.2-7.5 5.2A9.96 9.96 0 0012 22a9.96 9.96 0 007.5-4.8c-1-3-3.9-5.2-7.5-5.2z"
    />
  </svg>
);

const Navigation: React.FC = () => {
  const { authState, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const canChat = !!authState.user && isArtistRole(authState.user.role);
  const { unreadCount } = useUnreadMessages(canChat, authState.user?.id);
  const unreadLabel = unreadCount > 9 ? '9+' : String(unreadCount);
  const isProducer =
    authState.user?.role === 'producer' || authState.user?.role === 'producer_rapper';
  const { pendingCount } = usePendingVerseCount(!!isProducer);
  const pendingLabel = pendingCount > 9 ? '9+' : String(pendingCount);

  // Desktop nav kicks in at lg (1024px) — close the hamburger panel if the
  // viewport grows past that so tablet/mobile menu doesn't linger.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const messagesLink = canChat && (
    <Link to="/messages" className={iconLinkClass} aria-label="Messages" title="Messages">
      <MessagesIcon />
      {unreadCount > 0 && (
        <span
          className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-primary text-white text-[0.65rem] font-bold leading-none ring-2 ring-background-dark"
          aria-hidden
        >
          {unreadLabel}
        </span>
      )}
      <span className="sr-only">
        {unreadCount > 0 ? `${unreadCount} unread messages` : 'No unread messages'}
      </span>
    </Link>
  );

  const submissionsLink = isProducer && (
    <Link
      to="/submissions"
      className={iconLinkClass}
      aria-label="Verse submissions"
      title="Verse submissions"
    >
      <SubmissionsIcon />
      {pendingCount > 0 && (
        <span
          className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-primary text-white text-[0.65rem] font-bold leading-none ring-2 ring-background-dark"
          aria-hidden
        >
          {pendingLabel}
        </span>
      )}
      <span className="sr-only">
        {pendingCount > 0 ? `${pendingCount} verses waiting for review` : 'No verses waiting'}
      </span>
    </Link>
  );

  const profileLink = authState.user && (
    <Link
      to={`/profile/${authState.user.id}`}
      className={iconLinkClass}
      aria-label="My profile"
      title="My profile"
    >
      <ProfileIcon />
    </Link>
  );

  return (
    <nav className="bg-background-dark border-b border-background-tertiary shadow-soft sticky top-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto container-padding">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center flex-1 min-w-0">
            <Link to="/" className="text-2xl font-bold gradient-text whitespace-nowrap flex-shrink-0">
              DHH Collab
            </Link>
            <div className="hidden lg:flex lg:ml-12 lg:space-x-8">
              {authState.user && (
                <>
                  <Link
                    to="/home"
                    className="inline-flex items-center px-3 py-2 text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                  >
                    Home
                  </Link>
                  <Link
                    to="/cypher"
                    className="inline-flex items-center px-3 py-2 text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                  >
                    Cypher
                  </Link>
                  <Link
                    to="/search"
                    className="inline-flex items-center px-3 py-2 text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                  >
                    Find Artists
                  </Link>
                  {(authState.user.role === 'producer' || authState.user.role === 'producer_rapper') && (
                    <Link
                      to="/upload"
                      className="inline-flex items-center px-3 py-2 text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                    >
                      Upload Beat
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            {!authState.user ? (
              <>
                <Link
                  to="/login"
                  className="text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                >
                  Login
                </Link>
                <Link to="/signup" className="btn btn-primary">
                  Create an account
                </Link>
              </>
            ) : (
              <>
                {submissionsLink}
                {messagesLink}
                {profileLink}
                <button
                  onClick={handleSignOut}
                  className="ml-2 text-text-secondary hover:text-primary transition-all duration-300 font-medium"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>

          {/* Mobile: quick icons stay in the bar, the rest lives in the menu */}
          <div className="flex lg:hidden items-center gap-1 ml-2">
            {authState.user && (
              <>
                {submissionsLink}
                {messagesLink}
                {profileLink}
              </>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-xl text-text-secondary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300"
              aria-label="Open main menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-background-dark border-t border-background-tertiary w-full shadow-medium animate-fade-in">
          <div className="flex flex-col w-full divide-y divide-background-tertiary">
            {authState.user ? (
              <>
                <Link
                  to="/home"
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/cypher"
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Cypher
                </Link>
                <Link
                  to="/search"
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Find Artists
                </Link>
                {(authState.user.role === 'producer' || authState.user.role === 'producer_rapper') && (
                  <Link
                    to="/upload"
                    className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Upload Beat
                  </Link>
                )}
                <button
                  onClick={() => { handleSignOut(); setIsMenuOpen(false); }}
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/login"
                  className="block w-full px-6 py-4 text-text-secondary hover:text-primary transition-all duration-300 text-center font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="block w-full px-6 py-4 bg-primary text-white transition-all duration-300 text-center font-semibold"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Create an account
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
